/**
 * Student Dashboard Data Hook
 * Main hook that fetches and manages all student dashboard data
 *
 * This is the modularized version that imports utilities from sub-modules.
 */

import { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import type { DashboardData, SupplementProgress } from "./types";
import { getActivityDashboardSummary } from "../../services/activitiesApi";
import {
  getResearchStats,
  getDailyActivityLast7Days,
} from "../../services/researchChatApi";
import {
  getCurrentStudentId,
  getMostRecentDraft,
  getEssaysWithDraftCount,
  getStudentSupplementsProgress,
  getDetailedSupplementsProgress,
} from "../../services/essayDbApi";
import { getLatestEssayQuality } from "../../services/essayChatApi";
import { getCollegesByIds } from "../../services/college";
import { useCollegeStore } from "../../store/useCollegeStore";

// Import from sub-modules
import {
  formatRelativeTime,
  cleanHtmlContent,
  computeSubmissionStatus,
  computeModuleStatus,
} from "./utils";
import { generateFocusTasks } from "./focusTasks";
import { fetchRoadToSubmissionData } from "./roadToSubmission";

// GLOBAL CACHE (Stale-While-Revalidate pattern)
// We store the promise too to deduplicate requests if multiple components mount
const dashboardCache = {
  data: null as DashboardData | null,
  timestamp: 0,
  activeRequest: null as Promise<DashboardData | null> | null,
};

// Cache duration: 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

// Export cache invalidation for mutations
export const invalidateDashboardCache = () => {
  dashboardCache.data = null;
  dashboardCache.timestamp = 0;
  dashboardCache.activeRequest = null;
};

/**
 * Generate empty dashboard data structure (to be populated with real API data)
 */
export function generateEmptyDashboardData(): DashboardData {
  return {
    summary: {
      applicationHealthPercent: 0,
      completedModules: 0,
      inProgressModules: 0,
      notStartedModules: 6,
    },
    modules: [
      {
        id: "colleges",
        name: "Colleges",
        percentComplete: 0,
        status: "at_risk",
        stats: { totalColleges: 0, reach: 0, target: 0, safety: 0 },
        isCritical: false,
      },
      {
        id: "essays",
        name: "Essays",
        percentComplete: 0,
        status: "at_risk",
        stats: {
          essaysDrafted: 0,
          essaysTotal: 0,
          wordCount: 0,
          maxWords: 650,
        },
        isCritical: true,
      },
      {
        id: "supplements",
        name: "Supplements",
        percentComplete: 0,
        status: "at_risk",
        stats: { promptsCompleted: 0, promptsTotal: 0 },
        isCritical: true,
      },
      {
        id: "activities",
        name: "Activities",
        percentComplete: 0,
        status: "at_risk",
        stats: { slotsFilled: 0, totalSlots: 10 },
        isCritical: false,
      },
      {
        id: "rate_my_chance",
        name: "Rate My Chance",
        percentComplete: 0,
        status: "at_risk",
        stats: { topChance: 0 },
        isCritical: false,
      },
      {
        id: "research",
        name: "Research",
        percentComplete: 0,
        status: "at_risk",
        stats: { totalQuestions: 0 },
        isCritical: false,
      },
    ],
    todayFocus: [],
    focusTasks: [],
    essaysSummary: {
      totalEssays: 0,
      draftedEssays: 0,
      currentEssay: {
        id: "",
        title: "",
        wordCount: 0,
        maxWords: 650,
        lastEdited: "",
        draftNumber: 0,
        qualityBreakdown: {
          quality: 0,
          structure: 0,
          voice: 0,
          specificity: 0,
        },
        snippet: "",
        improvementChips: [],
      },
    },
    supplementsSummary: {
      totalPrompts: 0,
      completedPrompts: 0,
      byCollege: [],
    },
    collegesSummary: {
      totalColleges: 0,
      reachCount: 0,
      targetCount: 0,
      safetyCount: 0,
      balanceStatus: "balanced",
      reachColleges: [],
      targetColleges: [],
      safetyColleges: [],
    },
    rateMyChanceSummary: {
      averageScore: 0,
      bestChance: 0,
      worstChance: 0,
      topColleges: [],
    },
    activitiesSummary: {
      slotsFilled: 0,
      totalSlots: 10,
      setsTotal: 0,
      currentSet: {
        name: "Common App Activities",
        filledSlots: 0,
        totalSlots: 10,
      },
      composition: {
        academic: 0,
        leadership: 0,
        service: 0,
        arts: 0,
      },
      aiInsight: "",
    },
    researchSummary: {
      totalQuestions: 0,
      byCategory: [],
      dailyActivityLast7Days: [0, 0, 0, 0, 0, 0, 0],
      suggestedQuestions: [
        "What are the most important factors in college admissions?",
        "How can I improve my chances at reach schools?",
      ],
    },
    calendarSummary: {
      nextDeadline: null,
      upcomingCount: 0,
      overdueCount: 0,
      next10Days: [],
    },
    roadToSubmission: {
      selectedCollegeId: "",
      colleges: [],
    },
  };
}

/**
 * Hook to fetch and manage student dashboard data
 *
 * @returns {Object} Dashboard data, loading state, and error
 * @returns {DashboardData | null} data - The dashboard data or null if loading/error
 * @returns {boolean} loading - True while fetching data
 * @returns {string | null} error - Error message if fetch failed, null otherwise
 *
 * @example
 * const { data, loading, error } = useStudentDashboardData();
 *
 * if (loading) return <Spinner />;
 * if (error) return <Error message={error} />;
 * return <Dashboard data={data} />;
 */
export function useStudentDashboardData(): {
  data: DashboardData | null;
  loading: boolean;
  error: string | null;
} {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get favorites from Zustand store (localStorage) for fallback
  const localFavorites = useCollegeStore((state) => state.favorites);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      // 1. Check cache validity
      const now = Date.now();
      const isCacheValid =
        dashboardCache.data && now - dashboardCache.timestamp < CACHE_TTL;

      // 2. Serve efficient cache immediately if available
      if (dashboardCache.data) {
        if (isMounted) {
          setData(dashboardCache.data);
          // Only stop loading if cache is considered fresh enough to not need immediate refresh
          // But to be safe and ensure "stale-while-revalidate" behavior, we keep loading false
          // and let the background fetch update the data if needed
          setLoading(false);
        }

        if (isCacheValid && !dashboardCache.activeRequest) {
          return;
        }      } else {
        if (isMounted) setLoading(true);
      }

      // 3. Define the fetch function
      const executeFetch = async (): Promise<DashboardData> => {
        try {
          // Start with empty data structure
          const dashboardData = generateEmptyDashboardData();

          // Declare variables in outer scope for Road to Submission usage
          let supplementProgress: { completed: number; total: number } | null =
            null;
          let detailedProgress: SupplementProgress[] = [];
          let activityComposition: Record<string, number> = {};

          // [REAL DATA] Overwrite with real data from APIs
          try {
            const studentId = await getCurrentStudentId();
            if (studentId) {
              // Use Promise.allSettled to handle individual failures gracefully
              const results = await Promise.allSettled([
                getMostRecentDraft(studentId),
                getEssaysWithDraftCount(studentId),
                getStudentSupplementsProgress(studentId),
                getDetailedSupplementsProgress(studentId),
                getActivityDashboardSummary(studentId),
                getResearchStats(studentId),
                getDailyActivityLast7Days(studentId),
              ]);

              // Extract results, handling failures gracefully
              const recentDraft =
                results[0].status === "fulfilled" ? results[0].value : null;
              const essays =
                results[1].status === "fulfilled" ? results[1].value : [];
              const suppProgressRaw =
                results[2].status === "fulfilled" ? results[2].value : null;
              const detailedProgressRaw =
                results[3].status === "fulfilled" ? results[3].value : [];
              const activityStats =
                results[4].status === "fulfilled" ? results[4].value : null;
              const researchStats =
                results[5].status === "fulfilled" ? results[5].value : null;
              const dailyActivity =
                results[6].status === "fulfilled" ? results[6].value : [];

              // Log any failures
              results.forEach((result, index) => {
                if (result.status === "rejected") {
                  const apiNames = [
                    "getMostRecentDraft",
                    "getEssaysWithDraftCount",
                    "getStudentSupplementsProgress",
                    "getDetailedSupplementsProgress",
                    "getActivityDashboardSummary",
                    "getResearchStats",
                    "getDailyActivityLast7Days",
                  ];
                  // API failed silently - dashboard will show partial data
                }
              });

              supplementProgress = suppProgressRaw;
              detailedProgress = detailedProgressRaw;

              // Process essay data
              if (recentDraft) {
                const essayInfo = recentDraft.essays as { title?: string } | null;
                const essayTitle = essayInfo?.title || "Untitled Essay";
                const essayId = recentDraft.essay_id;
                const wordCount = recentDraft.word_count || 0;
                const maxWords = 650;

                // Fetch real quality scores from database
                let qualityBreakdown = {
                  quality: 0,
                  structure: 0,
                  voice: 0,
                  specificity: 0,
                };
                let improvementChips: string[] = [];

                try {
                  const qualityData = await getLatestEssayQuality(essayId);
                  if (qualityData) {
                    qualityBreakdown = {
                      quality: Math.round(
                        (qualityData.structure_score +
                          qualityData.voice_score +
                          qualityData.specificity_score) /
                        3,
                      ),
                      structure: qualityData.structure_score,
                      voice: qualityData.voice_score,
                      specificity: qualityData.specificity_score,
                    };
                    improvementChips = qualityData.feedback_keywords || [];
                  }
                } catch (qualityError) {                }

                // Clean up HTML content for snippet display
                const rawContent = recentDraft.content || "";
                const cleanContent = cleanHtmlContent(rawContent);
                const snippetText =
                  cleanContent.substring(0, 350) +
                  (cleanContent.length > 350 ? "..." : "");

                dashboardData.essaysSummary = {
                  totalEssays: essays.length,
                  draftedEssays: essays.filter((e) => e.draft_count > 0).length,
                  currentEssay: {
                    id: essayId,
                    title: essayTitle,
                    wordCount: wordCount,
                    maxWords: maxWords,
                    lastEdited: formatRelativeTime(recentDraft.updated_at),
                    draftNumber: recentDraft.draft_number,
                    qualityBreakdown: qualityBreakdown,
                    snippet: snippetText,
                    improvementChips: improvementChips,
                  },
                };

                // Update Essays Module
                const essayPercent = Math.min(
                  100,
                  Math.round((wordCount / 600) * 100),
                );
                const essaysModuleIndex = dashboardData.modules.findIndex(
                  (m) => m.id === "essays",
                );
                if (essaysModuleIndex !== -1) {
                  dashboardData.modules[essaysModuleIndex] = {
                    ...dashboardData.modules[essaysModuleIndex],
                    percentComplete: essayPercent,
                    status: computeModuleStatus(essayPercent),
                    stats: {
                      wordCount: wordCount,
                      maxWords: maxWords,
                    },
                  };
                }
              }

              // Update Supplements Module
              if (detailedProgress && detailedProgress.length > 0) {
                const totalCompleted = detailedProgress.reduce(
                  (acc, curr) => acc + curr.completed,
                  0,
                );
                const totalPrompts = detailedProgress.reduce(
                  (acc, curr) => acc + curr.total,
                  0,
                );

                const percent =
                  totalPrompts > 0
                    ? Math.round((totalCompleted / totalPrompts) * 100)
                    : 0;

                const supplementsModuleIndex = dashboardData.modules.findIndex(
                  (m) => m.id === "supplements",
                );
                if (supplementsModuleIndex !== -1) {
                  dashboardData.modules[supplementsModuleIndex] = {
                    ...dashboardData.modules[supplementsModuleIndex],
                    percentComplete: percent,
                    status: computeModuleStatus(percent),
                    stats: {
                      promptsCompleted: totalCompleted,
                      promptsTotal: totalPrompts,
                    },
                  };
                }

                // Update Summary and ByCollege breakdown
                if (dashboardData.supplementsSummary) {
                  const collegesWithPrompts = detailedProgress.filter(
                    (p) => p.total > 0,
                  );
                  dashboardData.supplementsSummary = {
                    ...dashboardData.supplementsSummary,
                    totalPrompts: totalPrompts,
                    completedPrompts: totalCompleted,
                    byCollege: collegesWithPrompts.map((p) => ({
                      collegeId: p.collegeId,
                      collegeName: p.collegeName,
                      promptsCompleted: p.completed,
                      promptsTotal: p.total,
                      daysLeft: 14, // Placeholder
                      completion:
                        p.total > 0
                          ? Math.round((p.completed / p.total) * 100)
                          : 0,
                    })),
                  };
                }
              } else if (supplementProgress) {
                // Fallback to simple progress
                const { completed, total } = supplementProgress;
                const percent =
                  total > 0 ? Math.round((completed / total) * 100) : 0;

                const supplementsModuleIndex = dashboardData.modules.findIndex(
                  (m) => m.id === "supplements",
                );
                if (supplementsModuleIndex !== -1) {
                  dashboardData.modules[supplementsModuleIndex] = {
                    ...dashboardData.modules[supplementsModuleIndex],
                    percentComplete: percent,
                    status: computeModuleStatus(percent),
                    stats: {
                      promptsCompleted: completed,
                      promptsTotal: total,
                    },
                  };
                }
              }

              // Update Activities with real data
              if (activityStats) {
                const totalActivities = activityStats.count;
                const totalSlots = 10;
                const percentComplete = Math.round(
                  (totalActivities / totalSlots) * 100,
                );

                Object.entries(activityStats.categories).forEach(
                  ([category, count]) => {
                    activityComposition[category] =
                      totalActivities > 0
                        ? Math.round((count / totalActivities) * 100)
                        : 0;
                  },
                );

                const activitiesModuleIndex = dashboardData.modules.findIndex(
                  (m) => m.id === "activities",
                );
                if (activitiesModuleIndex !== -1) {
                  dashboardData.modules[activitiesModuleIndex] = {
                    ...dashboardData.modules[activitiesModuleIndex],
                    percentComplete: percentComplete,
                    status: computeModuleStatus(percentComplete),
                    stats: {
                      slotsFilled: totalActivities,
                      totalSlots: totalSlots,
                    },
                  };
                }

                if (dashboardData.activitiesSummary) {
                  dashboardData.activitiesSummary = {
                    ...dashboardData.activitiesSummary,
                    slotsFilled: totalActivities,
                    currentSet: {
                      name: "Common App Activities",
                      filledSlots: totalActivities,
                      totalSlots: totalSlots,
                    },
                    composition: activityComposition,
                  };
                }
              }

              // Update Research Summary with real data
              if (researchStats && dashboardData.researchSummary) {
                const byCategory = Object.entries(researchStats.breakdown).map(
                  ([label, count]) => ({
                    label,
                    count,
                  }),
                );

                const researchPercent = Math.min(
                  100,
                  Math.round((researchStats.total / 10) * 100),
                );

                const researchModuleIndex = dashboardData.modules.findIndex(
                  (m) => m.id === "research",
                );
                if (researchModuleIndex !== -1) {
                  dashboardData.modules[researchModuleIndex] = {
                    ...dashboardData.modules[researchModuleIndex],
                    percentComplete: researchPercent,
                    status: computeModuleStatus(researchPercent),
                    stats: {
                      totalQuestions: researchStats.total,
                    },
                  };
                }

                dashboardData.researchSummary = {
                  ...dashboardData.researchSummary,
                  totalQuestions: researchStats.total,
                  byCategory:
                    byCategory.length > 0
                      ? byCategory
                      : dashboardData.researchSummary.byCategory,
                  dailyActivityLast7Days: dailyActivity,
                };
              }
            }
          } catch (realDataError) {
            console.error(
              "❌ [Dashboard] Error loading real data:",
              realDataError,
            );
          }

          // Initial Application Health calculation
          if (dashboardData.modules.length > 0) {
            const totalPercent = dashboardData.modules.reduce(
              (sum, m) => sum + m.percentComplete,
              0,
            );
            dashboardData.summary.applicationHealthPercent = Math.round(
              totalPercent / dashboardData.modules.length,
            );
          }

          // [REAL DATA] Fetch Road to Submission from favorites and deadlines
          try {
            const getModulePercent = (id: string) =>
              dashboardData.modules.find((m) => m.id === id)?.percentComplete ||
              0;

            const globalStats = {
              collegesPercent: getModulePercent("colleges"),
              essaysPercent: getModulePercent("essays"),
              activitiesPercent: getModulePercent("activities"),
              rateMyChancePercent: getModulePercent("rate_my_chance"),
              researchPercent: getModulePercent("research"),
            };

            const roadToSubmissionData = await fetchRoadToSubmissionData(
              localFavorites,
              detailedProgress,
              globalStats,
            );

            if (roadToSubmissionData.colleges.length > 0) {
              dashboardData.roadToSubmission = roadToSubmissionData;

              // Update collegesSummary based on favorite colleges
              const {
                data: { user },
              } = await supabase.auth.getUser();
              let studentProfile = {
                gpa: null as number | null,
                sat: null as number | null,
              };

              if (user) {
                const { data: profile } = await supabase
                  .from("students")
                  .select("gpa, sat_score, act_score")
                  .eq("auth_user_id", user.id)
                  .single();

                if (profile) {
                  studentProfile = {
                    gpa: profile.gpa,
                    sat: profile.sat_score,
                  };
                }
              }

              // Count unique colleges
              const uniqueCollegeNames = new Set(
                roadToSubmissionData.colleges.map((c) => c.name),
              );
              const totalFavorites = uniqueCollegeNames.size;

              // Calculate Reach/Target/Safety
              let reachCount = 0;
              let targetCount = 0;
              let safetyCount = 0;
              const reachColleges: { id: string; name: string }[] = [];
              const targetColleges: { id: string; name: string }[] = [];
              const safetyColleges: { id: string; name: string }[] = [];

              const favoriteCollegeIds = [
                ...new Set(
                  roadToSubmissionData.colleges.map((c) => c.id.split("-")[0]),
                ),
              ];

              if (favoriteCollegeIds.length > 0) {
                try {
                  const collegeDetails =
                    await getCollegesByIds(favoriteCollegeIds);
                  const { classifyCollege } =
                    await import("../../utils/classification");

                  collegeDetails.forEach((college) => {
                    const classification = classifyCollege(studentProfile, {
                      gpa: college.gpa,
                      sat: college.sat,
                      acceptanceRate: college.acceptanceRate,
                    });

                    const collegeInfo = { id: college.id, name: college.name };

                    if (classification === "Reach") {
                      reachCount++;
                      reachColleges.push(collegeInfo);
                    } else if (classification === "Safety") {
                      safetyCount++;
                      safetyColleges.push(collegeInfo);
                    } else {
                      targetCount++;
                      targetColleges.push(collegeInfo);
                    }
                  });
                } catch (err) {
                  console.error("Error calculating classification:", err);
                }
              }

              // Calculate balance status
              const counts = [reachCount, targetCount, safetyCount];
              const maxCount = Math.max(...counts);
              const otherCounts = counts.filter((c) => c !== maxCount);

              let balanceStatus: "balanced" | "reach_heavy" | "safety_heavy" =
                "balanced";

              if (totalFavorites > 0) {
                const thirtyPercentMoreThanBoth = otherCounts.every(
                  (c) => maxCount >= c * 1.3,
                );
                const fiftyPercentMoreThanOne = otherCounts.some(
                  (c) => maxCount >= c * 1.5,
                );

                if (thirtyPercentMoreThanBoth || fiftyPercentMoreThanOne) {
                  if (reachCount === maxCount) {
                    balanceStatus = "reach_heavy";
                  } else if (safetyCount === maxCount) {
                    balanceStatus = "safety_heavy";
                  }
                }
              }

              dashboardData.collegesSummary = {
                totalColleges: totalFavorites,
                reachCount,
                targetCount,
                safetyCount,
                balanceStatus,
                reachColleges,
                targetColleges,
                safetyColleges,
              };

              // Update Colleges module
              const collegesPercent = Math.min(
                100,
                Math.round((totalFavorites / 10) * 100),
              );
              const collegesModuleIndex = dashboardData.modules.findIndex(
                (m) => m.id === "colleges",
              );
              if (collegesModuleIndex !== -1) {
                dashboardData.modules[collegesModuleIndex] = {
                  ...dashboardData.modules[collegesModuleIndex],
                  percentComplete: collegesPercent,
                  status: computeModuleStatus(collegesPercent),
                  stats: {
                    totalColleges: totalFavorites,
                    reach: reachCount,
                    target: targetCount,
                    safety: safetyCount,
                  },
                };
              }

              // Update rateMyChanceSummary
              const uniqueCollegesMap = new Map();
              roadToSubmissionData.colleges.forEach((c) => {
                const baseId = c.id.split("-")[0];
                if (!uniqueCollegesMap.has(baseId)) {
                  uniqueCollegesMap.set(baseId, c);
                }
              });
              const uniqueColleges = Array.from(uniqueCollegesMap.values());

              let totalChanceScore = 0;
              const topChancesList: {
                collegeName: string;
                chancePercent: number;
                chanceLabel: "High" | "Medium" | "Low";
                trend: "up" | "down" | "flat";
              }[] = [];

              if (uniqueColleges.length > 0) {
                const { classifyCollege } =
                  await import("../../utils/classification");
                const uniqueIds = uniqueColleges.map((c) => c.id.split("-")[0]);
                const distinctIds = [...new Set(uniqueIds)];

                if (distinctIds.length > 0) {
                  try {
                    const studentId = await getCurrentStudentId();
                    const collegeDetails = await getCollegesByIds(distinctIds);

                    const { data: realChances } = await supabase
                      .from('student_college_acceptance_chances')
                      .select('college_id, acceptance_chance')
                      .eq('student_id', studentId)
                      .in('college_id', distinctIds.map(id => parseInt(id)));

                    collegeDetails.forEach((college) => {
                      const classification = classifyCollege(studentProfile, {
                        gpa: college.gpa,
                        sat: college.sat,
                        acceptanceRate: college.acceptanceRate,
                      });

                      let prob = 0.30;
                      let label: "High" | "Medium" | "Low" = "Low";

                      // Try to find real pre-computed chance
                      const cscId = parseInt(college.id);
                      const realEntry = realChances?.find(r => r.college_id === cscId);

                      if (realEntry) {
                        prob = realEntry.acceptance_chance;
                      } else {
                        // Fallback heuristic
                        if (classification === "Safety") prob = 0.90;
                        else if (classification === "Target") prob = 0.60;
                        else prob = 0.30;
                      }

                      // Set label for display
                      const scorePercent = Math.round(prob * 100);
                      if (scorePercent >= 75) label = "High";
                      else if (scorePercent >= 40) label = "Medium";

                      totalChanceScore += prob; // Accumulate probability (Expected Value)


                      topChancesList.push({
                        collegeName: college.name,
                        chancePercent: scorePercent,
                        chanceLabel: label,
                        trend: "flat",
                      });
                    });
                  } catch (err) {
                    console.error("Error calculating chances:", err);
                  }
                }
              }

              topChancesList.sort((a, b) => b.chancePercent - a.chancePercent);

              // Health Score: Aim for >3 estimated acceptances equals 100% health.
              // Logic: (Expected / 3) * 100, capped at 100.
              const expectedAcceptances = parseFloat(totalChanceScore.toFixed(1));
              const healthScore = Math.min(100, Math.round((totalChanceScore / 3) * 100));

              dashboardData.rateMyChanceSummary = {
                averageScore: expectedAcceptances,
                bestChance: Math.max(
                  ...topChancesList.map((c) => c.chancePercent),
                  0,
                ),
                worstChance: Math.min(
                  ...topChancesList.map((c) => c.chancePercent),
                  0,
                ),
                topColleges: topChancesList.slice(0, 3),
              };

              // Update Rate My Chance module
              const rateMyChanceModuleIndex = dashboardData.modules.findIndex(
                (m) => m.id === "rate_my_chance",
              );
              if (rateMyChanceModuleIndex !== -1) {
                dashboardData.modules[rateMyChanceModuleIndex] = {
                  ...dashboardData.modules[rateMyChanceModuleIndex],
                  percentComplete: healthScore,
                  status: computeModuleStatus(healthScore),
                  stats: {
                    topChance: Math.max(
                      ...topChancesList.map((c) => c.chancePercent),
                      0,
                    ),
                  },
                };
              }

              // Recalculate Road to Submission readiness with Rate My Chance
              if (
                dashboardData.roadToSubmission &&
                dashboardData.roadToSubmission.colleges.length > 0
              ) {
                const getModulePercent2 = (id: string) =>
                  dashboardData.modules.find((m) => m.id === id)
                    ?.percentComplete || 0;
                const updatedGlobalStats = {
                  collegesPercent: getModulePercent2("colleges"),
                  essaysPercent: getModulePercent2("essays"),
                  activitiesPercent: getModulePercent2("activities"),
                  rateMyChancePercent: healthScore,
                  researchPercent: getModulePercent2("research"),
                };

                dashboardData.roadToSubmission.colleges =
                  dashboardData.roadToSubmission.colleges.map((college) => {
                    const suppProgress = detailedProgress.find(
                      (p) =>
                        String(p.collegeId) === college.id.split("-")[0] ||
                        String(p.collegeId).trim() ===
                        college.id.split("-")[0].trim(),
                    );
                    let suppPercent = 0;
                    if (suppProgress) {
                      if (suppProgress.total > 0) {
                        suppPercent = Math.min(
                          100,
                          Math.round(
                            (suppProgress.completed / suppProgress.total) * 100,
                          ),
                        );
                      } else {
                        suppPercent = 100;
                      }
                    }

                    const totalScore =
                      updatedGlobalStats.collegesPercent +
                      updatedGlobalStats.essaysPercent +
                      suppPercent +
                      updatedGlobalStats.activitiesPercent +
                      updatedGlobalStats.rateMyChancePercent +
                      updatedGlobalStats.researchPercent;

                    const newReadiness = Math.round(totalScore / 6);

                    return {
                      ...college,
                      readinessPercent: newReadiness,
                      status: computeSubmissionStatus(
                        college.deadline,
                        newReadiness,
                        college.expectedPercentToday,
                      ),
                    };
                  });
              }

              // Update calendarSummary
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              const allDeadlines = roadToSubmissionData.colleges.map(
                (college) => ({
                  date: new Date(college.deadline),
                  label: `${college.name} ${college.shortLabel || college.decisionType}`,
                  collegeId: college.id,
                  readinessPercent: college.readinessPercent,
                }),
              );

              const upcomingDeadlines = allDeadlines.filter((d) => {
                const deadlineDate = new Date(d.date);
                deadlineDate.setHours(0, 0, 0, 0);
                return deadlineDate >= today;
              });
              const overdueDeadlines = allDeadlines.filter((d) => {
                const deadlineDate = new Date(d.date);
                deadlineDate.setHours(0, 0, 0, 0);
                return deadlineDate < today;
              });

              const sortedUpcoming = [...upcomingDeadlines].sort(
                (a, b) => a.date.getTime() - b.date.getTime(),
              );
              const nextDeadline = sortedUpcoming[0];

              const next10Days = [];
              for (let i = 1; i <= 10; i++) {
                const futureDate = new Date(today);
                futureDate.setDate(futureDate.getDate() + i);
                const dayOfMonth = futureDate.getDate();

                const hasDeadline = allDeadlines.some((d) => {
                  const deadlineDate = new Date(d.date);
                  deadlineDate.setHours(0, 0, 0, 0);
                  return deadlineDate.getTime() === futureDate.getTime();
                });

                const isOverdue = allDeadlines.some((d) => {
                  const deadlineDate = new Date(d.date);
                  deadlineDate.setHours(0, 0, 0, 0);
                  return (
                    deadlineDate.getTime() === futureDate.getTime() &&
                    deadlineDate < today
                  );
                });

                next10Days.push({
                  day: dayOfMonth,
                  hasDeadline,
                  isOverdue,
                });
              }

              dashboardData.calendarSummary = {
                nextDeadline: nextDeadline
                  ? {
                    label: nextDeadline.label,
                    date: nextDeadline.date.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    }),
                    readinessPercent: nextDeadline.readinessPercent || 0,
                    readinessText: nextDeadline.readinessPercent
                      ? `${nextDeadline.readinessPercent}% ready`
                      : "",
                  }
                  : null,
                upcomingCount: upcomingDeadlines.length,
                overdueCount: overdueDeadlines.length,
                next10Days: next10Days,
              };

              // Generate dynamic focus tasks
              const dynamicFocusTasks = generateFocusTasks(
                roadToSubmissionData.colleges,
                detailedProgress,
                activityComposition,
              );
              if (dynamicFocusTasks.length > 0) {
                dashboardData.focusTasks = dynamicFocusTasks;
              }
            }
          } catch (roadError) {
            console.error(
              "❌ [Dashboard] Error loading Road to Submission:",
              roadError,
            );
          }

          // Recalculate Application Health after all module updates are complete
          // This ensures Rate My Chance and other late-updated modules are included
          if (dashboardData.modules.length > 0) {
            const totalPercent = dashboardData.modules.reduce(
              (sum, m) => sum + m.percentComplete,
              0,
            );
            dashboardData.summary.applicationHealthPercent = Math.round(
              totalPercent / dashboardData.modules.length,
            );
          }

          // Update Cache
          dashboardCache.data = dashboardData;
          dashboardCache.timestamp = Date.now();

          return dashboardData;
        } catch (err) {
          console.error("❌ [Dashboard] Fatal error in fetchData:", err);
          throw err;
        }
      };

      // 4. Create and store the promise to deduplicate requests
      if (!dashboardCache.activeRequest) {
        dashboardCache.activeRequest = executeFetch();
      }

      try {
        const result = await dashboardCache.activeRequest;
        if (isMounted) {
          setData(result);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          const errorMessage =
            err instanceof Error
              ? err.message
              : "An error occurred while loading dashboard data";
          setError(errorMessage);
          // Still set empty data so the dashboard can render with empty state
          setData(generateEmptyDashboardData());
        }
      } finally {
        if (isMounted) setLoading(false);
        dashboardCache.activeRequest = null;
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [localFavorites]);

  return { data, loading, error };
}
